import {
  OrderProduct,
  OrderStatusCode,
  OutboundReferenceCollection,
  ProductInventoryDoc,
  ProductOutboundOperation,
  ProductOutboundTransaction,
  ProductTransactionStatus,
  StoreDoc,
} from '@gooduncles/gu-app-schema';
import { groupBy } from 'lodash-es';
import { promiseAllSettled } from 'src/utils/common';
import { getOrders, getProductInventory, getStore } from 'src/utils/firebase/firebase-short-cut';
import { generatePurchaseOrderMessage } from 'src/utils/supplier-purchase-order-util';
import useSWRImmutable from 'swr/immutable';

const productToOutboundTransaction = (
  product: OrderProduct & { storeId?: string },
  inventory?: ProductInventoryDoc
): ProductOutboundTransaction => {
  return {
    requestDate: '',
    status: ProductTransactionStatus.PENDING,
    inventoryId: inventory?._id ?? 'error',
    referenceCollection: OutboundReferenceCollection.PRODUCT_SUPPLIER,
    referenceId: 'preview',
    operation: ProductOutboundOperation.PURCHASE,
    storeId: product.storeId ?? null,
    productId: product.productId,
    requestQuantity: product.volume,
  };
};

export interface PurchaseOrderPreviewForSupplier {
  supplierId: string;
  messageRawList: string[];
  products: (OrderProduct & { storeId?: string; storeNickname?: string; inventory?: ProductInventoryDoc })[];
}

const getPurchaseOrderPreview = async (orderStatus: OrderStatusCode[]) => {
  const submittedOrders = await getOrders([['orderStatus', 'in', orderStatus]]);
  const { fulfilled: stores } = await promiseAllSettled(submittedOrders.map((order) => getStore(order.storeId)));

  const orderProductsWithStore = submittedOrders.flatMap((order) => {
    const store = (stores as StoreDoc[]).find((store) => store._id === order.storeId);
    const orderProducts = order.products.map((product) => {
      return {
        ...product,
        storeId: store?._id,
        storeNickname: store?.storeNickname,
      };
    });
    return orderProducts;
  });

  const orderProductsGroupBySupplierId = groupBy(orderProductsWithStore, (product) => product.supplierId);
  const purchaseOrderPreviewForSupplier: PurchaseOrderPreviewForSupplier[] = [];
  for (const [supplierId, products0] of Object.entries(orderProductsGroupBySupplierId)) {
    const purchaseOrderPreviewItems: string[] = [];
    const groupByInventoryId = groupBy(products0, (product) => product.inventoryId);
    const products = [];
    for (const [inventoryId, products1] of Object.entries(groupByInventoryId)) {
      const inventory = await getProductInventory(inventoryId);
      if (!inventory) {
        purchaseOrderPreviewItems.push(`재고 정보를 찾을 수 없습니다. 재고 정보: ${inventoryId}`);
        continue;
      }
      const purchaseTransactions = products1.map((product) => productToOutboundTransaction(product, inventory));
      purchaseOrderPreviewItems.push(generatePurchaseOrderMessage(inventory, purchaseTransactions));
      products.push(...products1.map((product) => ({ ...product, inventory })));
    }
    purchaseOrderPreviewForSupplier.push({
      supplierId,
      messageRawList: purchaseOrderPreviewItems,
      products,
    });
  }
  return purchaseOrderPreviewForSupplier;
};

const useSWRPurchaseOrderPreview = (orderStatus: OrderStatusCode[]) => {
  const { data, isLoading, error, mutate } = useSWRImmutable(
    `/purchase-order-preview/order/${orderStatus.join('/')}`,
    () => getPurchaseOrderPreview(orderStatus)
  );

  return {
    data,
    isLoading,
    error,
    refresh: () => mutate(undefined, { revalidate: true }),
  };
};

export default useSWRPurchaseOrderPreview;
